/* ------------------------------------------------------------------ */
/* UK Mortgage Calculator — defaults and calculation logic             */
/* ------------------------------------------------------------------ */

export interface MortgageInputs {
  propertyPrice: number
  depositPercent: number
  interestRate: number
  termYears: number
  isInterestOnly: boolean
}

export interface MortgageResult {
  loanAmount: number
  depositAmount: number
  ltv: number
  monthlyPayment: number
  monthlyInterestOnly: number
  totalRepaid: number
  totalInterest: number
}

export const MORTGAGE_DEFAULTS: MortgageInputs = {
  propertyPrice: 300_000,
  depositPercent: 10,
  interestRate: 4.5,
  termYears: 25,
  isInterestOnly: false,
} as const

export function calculateMortgage(inputs: MortgageInputs): MortgageResult {
  const depositAmount = inputs.propertyPrice * (inputs.depositPercent / 100)
  const loanAmount = inputs.propertyPrice - depositAmount
  const ltv = inputs.propertyPrice > 0
    ? ((loanAmount / inputs.propertyPrice) * 100)
    : 0

  if (loanAmount <= 0 || inputs.interestRate <= 0 || inputs.termYears <= 0) {
    return {
      loanAmount: Math.max(loanAmount, 0),
      depositAmount,
      ltv,
      monthlyPayment: 0,
      monthlyInterestOnly: 0,
      totalRepaid: 0,
      totalInterest: 0,
    }
  }

  const monthlyRate = inputs.interestRate / 100 / 12
  const totalMonths = inputs.termYears * 12

  // Interest-only monthly payment
  const monthlyInterestOnly = loanAmount * monthlyRate

  // Repayment monthly payment: M = P[r(1+r)^n] / [(1+r)^n – 1]
  const compoundFactor = Math.pow(1 + monthlyRate, totalMonths)
  const monthlyRepayment =
    (loanAmount * monthlyRate * compoundFactor) / (compoundFactor - 1)

  const monthlyPayment = inputs.isInterestOnly
    ? monthlyInterestOnly
    : monthlyRepayment

  const totalRepaid = inputs.isInterestOnly
    ? monthlyInterestOnly * totalMonths + loanAmount
    : monthlyRepayment * totalMonths

  const totalInterest = totalRepaid - loanAmount

  return {
    loanAmount,
    depositAmount,
    ltv: Math.round(ltv * 10) / 10,
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    monthlyInterestOnly: Math.round(monthlyInterestOnly * 100) / 100,
    totalRepaid: Math.round(totalRepaid),
    totalInterest: Math.round(totalInterest),
  }
}

export { formatCurrency, formatCurrencyPence } from "@/lib/format-currency"
